import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_NAME = "GharBhada";
const SITE_URL_FALLBACK = "https://gharbhada.org";
const SUPPORTED_CITY_SLUGS = new Set(["kathmandu", "lalitpur", "bhaktapur", "pokhara", "chitwan", "butwal", "dharan"]);
const SUPPORTED_CATEGORY_SLUGS = new Set([
  "gharbhada",
  "flat-bhada",
  "room-rent",
  "house-for-rent",
  "apartment-rent",
  "kotha-bhada",
  "jagga-bhada",
  "office-space-rent",
]);

function slugToTitle(slug) {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function ensureMeta(selector, attributes) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => {
      tag.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }
  return tag;
}

function setMetaContentByName(name, content) {
  const tag = ensureMeta(`meta[name='${name}']`, { name });
  tag.setAttribute("content", content);
}

function setMetaContentByProperty(property, content) {
  const tag = ensureMeta(`meta[property='${property}']`, { property });
  tag.setAttribute("content", content);
}

function setCanonical(url) {
  let canonical = document.head.querySelector("link[rel='canonical']");
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
}

function setAlternateHrefLang(hreflang, href) {
  let alternate = document.head.querySelector(`link[rel='alternate'][hreflang='${hreflang}']`);
  if (!alternate) {
    alternate = document.createElement("link");
    alternate.setAttribute("rel", "alternate");
    alternate.setAttribute("hreflang", hreflang);
    document.head.appendChild(alternate);
  }
  alternate.setAttribute("href", href);
}

function setJsonLd(schemaObject) {
  const id = "gb-seo-jsonld";
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(schemaObject);
}

function normalizeFindPropertySearch(search) {
  const incoming = new URLSearchParams(search || "");
  const allowed = ["city", "type", "minPrice", "maxPrice", "verified", "q"];
  const canonical = new URLSearchParams();

  allowed.forEach((key) => {
    const value = incoming.get(key);
    if (value && String(value).trim()) {
      canonical.set(key, String(value).trim());
    }
  });

  return canonical.toString();
}

function buildBreadcrumbSchema(siteUrl, pathname) {
  const chunks = pathname.split("/").filter(Boolean);
  const listItems = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
  ];

  let runningPath = "";
  chunks.forEach((chunk, idx) => {
    runningPath += `/${chunk}`;
    listItems.push({
      "@type": "ListItem",
      position: idx + 2,
      name: slugToTitle(chunk),
      item: `${siteUrl}${runningPath}`,
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: listItems,
  };
}

function getRouteMetadata(pathname, search = "") {
  if (pathname === "/") {
    return {
      title: "GharBhada Nepal | Verified Gharbhada, Kothabhada, Flat and Room Bhada",
      description:
        "Find verified rental homes in Nepal. Discover gharbhada, kothabhada, flat bhada, room bhada, and apartment rent in Kathmandu, Lalitpur, Bhaktapur, Pokhara, Chitwan, Butwal, and Dharan.",
      schema: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL_FALLBACK,
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL_FALLBACK}/find-property?search={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    };
  }

  if (pathname === "/find-property") {
    const params = new URLSearchParams(search || "");
    const city = params.get("city") || params.get("q") || "";
    const type = params.get("type") || "";
    const context = [type, city].filter(Boolean).join(" in ");
    const titleContext = context ? ` | ${context}` : "";
    return {
      title: `Find Property for Rent in Nepal${titleContext} | Flat, Room, House and Apartment Bhada`,
      description:
        "Browse verified rental listings in Nepal with filters by city, price, rooms, and amenities. Compare flat bhada, room bhada, and house rent options quickly.",
      schema: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Property Listings for Rent in Nepal",
        about: "Rental properties in Nepal",
      },
      canonicalSearch: normalizeFindPropertySearch(search),
      allowIndexWithSearch: Boolean(city || type),
    };
  }

  if (/^\/rent\/[a-z0-9-]+$/i.test(pathname)) {
    const citySlug = pathname.split("/")[2] || "";
    const city = slugToTitle(citySlug);
    return {
      title: `Rent in ${city} | Gharbhada, Flat Bhada, Room Rent | ${SITE_NAME}`,
      description: `Explore rental listings in ${city} including gharbhada, flat bhada, room rent, and house for rent with verified details and direct owner contact.`,
      schema: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `Rent in ${city}`,
        about: `${city} rental listings`,
      },
      validProgrammaticRoute: SUPPORTED_CITY_SLUGS.has(citySlug.toLowerCase()),
    };
  }

  if (/^\/rent\/[a-z0-9-]+\/[a-z0-9-]+$/i.test(pathname)) {
    const [, , citySlug, categorySlug] = pathname.split("/");
    const city = slugToTitle(citySlug);
    const category = slugToTitle(categorySlug);
    return {
      title: `${category} in ${city} | Verified Rentals | ${SITE_NAME}`,
      description: `Find ${category.toLowerCase()} listings in ${city} with verified details, rent comparisons, and direct owner contact on ${SITE_NAME}.`,
      schema: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${category} in ${city}`,
        about: `${category} rental listings in ${city}`,
      },
      validProgrammaticRoute:
        SUPPORTED_CITY_SLUGS.has(String(citySlug || "").toLowerCase()) &&
        SUPPORTED_CATEGORY_SLUGS.has(String(categorySlug || "").toLowerCase()),
    };
  }

  if (pathname.startsWith("/property/")) {
    const propertyId = pathname.split("/")[2];
    let listingName = "Property for Rent in Nepal";
    let listingDesc = "View verified property details, photos, amenities, and monthly rent in Nepal.";
    let priceValue;
    let city;
    let state;
    let propertyType = "Accommodation";

    try {
      const cached = sessionStorage.getItem(`property_${propertyId}`);
      if (cached) {
        const data = JSON.parse(cached);
        listingName = data?.name || listingName;
        city = data?.city;
        state = data?.state;
        priceValue = Number(data?.price) || undefined;
        propertyType = data?.type || propertyType;
        listingDesc = `${listingName}${city ? ` in ${city}` : ""}${state ? `, ${state}` : ""}. View photos, amenities, and contact owner directly on ${SITE_NAME}.`;
      }
    } catch {
      // Ignore malformed session cache and fall back to generic metadata.
    }

    return {
      title: `${listingName} | Rent in Nepal | ${SITE_NAME}`,
      description: listingDesc,
      schema: {
        "@context": "https://schema.org",
        "@type": "Offer",
        priceCurrency: "NPR",
        ...(priceValue ? { price: String(priceValue) } : {}),
        itemOffered: {
          "@type": "Accommodation",
          name: listingName,
          additionalType: propertyType,
          address: {
            "@type": "PostalAddress",
            ...(city ? { addressLocality: city } : {}),
            ...(state ? { addressRegion: state } : {}),
            addressCountry: "NP",
          },
        },
      },
    };
  }

  if (/^\/gharbhada-[a-z0-9-]+$/i.test(pathname)) {
    const city = pathname.replace("/gharbhada-", "").replace(/-/g, " ");
    return {
      title: `Gharbhada in ${city.replace(/\b\w/g, (m) => m.toUpperCase())} | Verified Rental Homes | ${SITE_NAME}`,
      description: `Find verified gharbhada listings in ${city}. Explore flats, rooms, and houses for rent with direct owner contact on ${SITE_NAME}.`,
      schema: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `Gharbhada in ${city}`,
      },
    };
  }

  if (/^\/flat-bhada-[a-z0-9-]+$/i.test(pathname)) {
    const city = pathname.replace("/flat-bhada-", "").replace(/-/g, " ");
    return {
      title: `Flat Bhada in ${city.replace(/\b\w/g, (m) => m.toUpperCase())} | Apartments for Rent | ${SITE_NAME}`,
      description: `Browse flat bhada and apartment rent options in ${city}. Compare rent, features, and locations in one place.`,
      schema: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `Flat Bhada in ${city}`,
      },
    };
  }

  if (/^\/room-rent-[a-z0-9-]+$/i.test(pathname)) {
    const city = pathname.replace("/room-rent-", "").replace(/-/g, " ");
    return {
      title: `Room Rent in ${city.replace(/\b\w/g, (m) => m.toUpperCase())} | Budget and Student Room Bhada | ${SITE_NAME}`,
      description: `Explore room rent listings in ${city} for students and professionals. Find budget-friendly kotha bhada with verified details.`,
      schema: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `Room Rent in ${city}`,
      },
    };
  }

  if (pathname === "/cheap-kotha-bhada-near-college") {
    return {
      title: `Cheap Kotha Bhada Near College | Student Rentals in Nepal | ${SITE_NAME}`,
      description:
        "Find affordable kotha bhada and room rent near colleges in Nepal. Compare prices, amenities, and availability quickly.",
      schema: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Cheap Kotha Bhada Near College",
      },
    };
  }

  if (pathname === "/about") {
    return {
      title: "About GharBhada | Nepal Rental Platform",
      description:
        "Learn how GharBhada helps renters and owners connect through verified rental listings, KYC-first trust, and local support across Nepal.",
      schema: {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "About GharBhada",
      },
    };
  }

  if (pathname === "/contact") {
    return {
      title: "Contact GharBhada | Rental Support in Nepal",
      description:
        "Contact GharBhada for rental listing support, legal/privacy requests, and safety reporting for Nepal property rentals.",
      schema: {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact GharBhada",
      },
    };
  }

  return {
    title: `${SITE_NAME} | Rental Homes in Nepal`,
    description:
      "Find verified rental homes in Nepal including flats, rooms, apartments, and houses for rent.",
    schema: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: SITE_NAME,
    },
  };
}

function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const siteUrl = (process.env.REACT_APP_SITE_URL || window.location.origin || SITE_URL_FALLBACK).replace(/\/$/, "");
    const pathname = location.pathname || "/";
    const metadata = getRouteMetadata(pathname, location.search);

    const canonicalSearch = metadata.canonicalSearch
      ? `?${metadata.canonicalSearch}`
      : pathname === "/find-property"
      ? ""
      : location.search;

    const currentUrl = `${siteUrl}${pathname}${canonicalSearch}`;
    const isPrivateRoute =
      pathname.startsWith("/admin") ||
      pathname === "/login" ||
      pathname === "/signup" ||
      pathname === "/forgot-password" ||
      pathname === "/reset-password" ||
      pathname === "/messages" ||
      pathname === "/owner-dashboard" ||
      pathname === "/renter-dashboard" ||
      pathname === "/profile" ||
      pathname === "/list-property" ||
      pathname.startsWith("/property-settings") ||
      pathname.startsWith("/apply/");
    const isFindPropertyWithUnscopedQuery = pathname === "/find-property" && Boolean(location.search) && !metadata.allowIndexWithSearch;
    const isInvalidProgrammaticRoute = typeof metadata.validProgrammaticRoute === "boolean" && !metadata.validProgrammaticRoute;
    const robotsContent =
      isPrivateRoute || isFindPropertyWithUnscopedQuery || isInvalidProgrammaticRoute
        ? "noindex, nofollow"
        : "index, follow";

    const organizationSchema = {
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
      name: SITE_NAME,
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/logo.png`,
        width: 512,
        height: 512,
      },
      image: `${siteUrl}/logo.png`,
      description:
        "GharBhada is Nepal\u2019s trusted rental platform connecting renters and owners directly \u2014 no broker, no dalali. Find verified gharbhada, flat bhada, room rent, and kotha bhada listings across Kathmandu, Pokhara, Lalitpur, Bhaktapur, Chitwan, Butwal, and Dharan.",
      email: "support@gharbhada.org",
      areaServed: "NP",
      foundingDate: "2024",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Kathmandu",
        addressRegion: "Bagmati Province",
        addressCountry: "NP",
      },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "support@gharbhada.org",
        areaServed: "NP",
        availableLanguage: ["English", "Nepali"],
      },
      sameAs: [
        "https://www.facebook.com/gharbhadarental",
        "https://www.instagram.com/gharbhada",
        "https://twitter.com/gharbhada",
      ],
    };

    const websiteSchema = {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      name: SITE_NAME,
      url: siteUrl,
      publisher: { "@id": `${siteUrl}/#organization` },
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${siteUrl}/find-property?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
      inLanguage: ["en-NP", "ne-NP"],
    };

    const breadcrumbSchema = buildBreadcrumbSchema(siteUrl, pathname);

    // Build page-level schema (strip WebSite from route schema since we always include it globally)
    const routeSchemaType = metadata.schema?.["@type"];
    const routeSchemaEntry =
      routeSchemaType === "WebSite"
        ? null
        : { ...metadata.schema, ...(metadata.schema?.url ? {} : { url: currentUrl }) };

    const graphNodes = [organizationSchema, websiteSchema];
    if (routeSchemaEntry) graphNodes.push(routeSchemaEntry);
    if (pathname !== "/") graphNodes.push(breadcrumbSchema);

    const graphSchema = {
      "@context": "https://schema.org",
      "@graph": graphNodes,
    };

    document.title = metadata.title;
    setCanonical(currentUrl);
    setAlternateHrefLang("en-NP", currentUrl);
    setAlternateHrefLang("ne-NP", currentUrl);
    setAlternateHrefLang("x-default", currentUrl);

    setMetaContentByName("description", metadata.description);
    setMetaContentByName("robots", robotsContent);

    setMetaContentByProperty("og:title", metadata.title);
    setMetaContentByProperty("og:description", metadata.description);
    setMetaContentByProperty("og:type", "website");
    setMetaContentByProperty("og:url", currentUrl);
    setMetaContentByProperty("og:site_name", SITE_NAME);

    setMetaContentByName("twitter:card", "summary_large_image");
    setMetaContentByName("twitter:title", metadata.title);
    setMetaContentByName("twitter:description", metadata.description);

    setJsonLd(graphSchema);
  }, [location.pathname, location.search]);

  return null;
}

export default SeoManager;